import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import useSWR, { mutate } from 'swr';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import { AccountSettingsDialog } from '@/components/ui/account-settings-dialog';
import {
  UsersIcon,
  Cog6ToothIcon,
  InboxIcon,
  PaperAirplaneIcon,
  CreditCardIcon,
  ArrowRightOnRectangleIcon,
  EnvelopeIcon,
  ListBulletIcon,
  SparklesIcon,
  ArrowsRightLeftIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { PanelLeft, PanelRight, Sparkles } from 'lucide-react';
import { CreditDisplay } from '@/components/CreditDisplay';
import { useConversations } from '@/lib/hooks/useMailbox';
import { getProductName, FIND_PEOPLE_ENABLED } from '@/lib/config';
import LogoIcon from '@/components/ui/logo-icon';
import { WorkspaceAvatar } from '@/components/ui/workspace-avatar';
import { AIPanel } from '@/components/ai-panel/AIPanel';

interface User {
  id: number;
  email: string;
  name?: string;
  role: string;
}

// Sidebar context
export const SidebarContext = createContext<{
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  isAgentPanelOpen: boolean;
  mounted: boolean;
}>({
  isSidebarOpen: true,
  toggleSidebar: () => {},
  setSidebarOpen: () => {},
  isAgentPanelOpen: false,
  mounted: false,
});

export const useSidebar = () => useContext(SidebarContext);

const fetcher = (url: string) => fetch(url, { credentials: 'include' }).then((res) => res.json());

type NavItem =
  | { href: string; icon: React.ComponentType<{ className?: string }>; label: string; disabled?: boolean; tooltip?: string }
  | { subheading: string };

const navItems: NavItem[] = [
  { href: '/dashboard/agent', icon: SparklesIcon, label: 'Agent' },
  { subheading: '' },
  ...(FIND_PEOPLE_ENABLED
    ? [{ href: '/dashboard/find-leads', icon: UsersIcon, label: 'Find Leads' } as NavItem]
    : []),
  { href: '/dashboard/lists', icon: ListBulletIcon, label: 'Lists' },
  { href: '/dashboard/campaigns', icon: PaperAirplaneIcon, label: 'Campaigns' },
  { subheading: '' },
  { href: '/dashboard/email-accounts', icon: EnvelopeIcon, label: 'Sender Accounts' },
  { href: '/dashboard/mailbox', icon: InboxIcon, label: 'Mailbox' },
  { subheading: '' },
  { href: '/dashboard/guides', icon: BookOpenIcon, label: 'Guides' },
];

function WorkspaceMenu({
  isSidebarOpen,
  onOpenAccountSettings
}: {
  isSidebarOpen: boolean;
  onOpenAccountSettings: (section?: string) => void;
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { data: team } = useSWR<{ name: string; id: number; avatarColor?: string; avatarUrl?: string }>('/api/team', fetcher);
  const { data: workspacesData } = useSWR<{ id: number; name: string; avatarColor?: string; avatarUrl?: string }[]>('/api/workspaces', fetcher);

  return (
    <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <DropdownMenuTrigger asChild>
        <div className={`flex items-center gap-2 cursor-pointer rounded-full py-2 transition-colors ${isSidebarOpen ? 'w-full bg-input-bg control-gradient border border-control-border shadow-lg pl-2.5 pr-4' : 'w-fit p-0'}`}>
          <Tooltip open={!isSidebarOpen ? undefined : false}>
            <TooltipTrigger asChild>
              <div>
                <WorkspaceAvatar
                  name={team?.name || 'Workspace'}
                  color={team?.avatarColor}
                  imageUrl={team?.avatarUrl}
                  size="md-sm"
                  className="cursor-pointer shadow-lg"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={10}>
              {team?.name || 'Workspace'}
            </TooltipContent>
          </Tooltip>
          {isSidebarOpen && (
            <span className={`font-normal truncate max-w-36 ${
              (team?.name || 'Workspace').length > 16 ? 'text-xs' : 'text-sm'
            }`}>
              {team?.name || 'Workspace'}
            </span>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="right" className="flex flex-col gap-1 w-64 p-2">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="cursor-pointer">
            <ArrowsRightLeftIcon className="h-4 w-4" />
            <span>Change Workspace</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent sideOffset={8} className="w-64">
            {(workspacesData || []).map((ws) => (
              <DropdownMenuItem
                key={ws.id}
                className="cursor-pointer !h-auto p-2"
                disabled={ws.id === team?.id}
                onClick={async () => {
                  if (ws.id === team?.id) return;
                  await fetch('/api/workspaces/switch', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ workspaceId: ws.id }),
                  });
                  window.location.href = '/dashboard/campaigns';
                }}
              >
                <WorkspaceAvatar name={ws.name} color={ws.avatarColor} imageUrl={ws.avatarUrl} size="sm" />
                {ws.name}
                {ws.id === team?.id && (
                  <span className="ml-auto text-xs text-muted-foreground">Current</span>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => {
            setIsMenuOpen(false);
            onOpenAccountSettings('workspaces');
          }}
        >
          <Cog6ToothIcon className="h-4 w-4" />
          Manage Workspaces
        </DropdownMenuItem>
        <Link to="/dashboard/workspace">
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => setIsMenuOpen(false)}
          >
            <SparklesIcon className="h-4 w-4" />
            AI Context
          </DropdownMenuItem>
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu({
  isSidebarOpen,
  onOpenAccountSettings
}: {
  isSidebarOpen: boolean;
  onOpenAccountSettings: (section?: string) => void;
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { data: user, mutate: mutateUser } = useSWR<User>('/api/user', fetcher);
  const navigate = useNavigate();

  useEffect(() => {
    mutateUser();
  }, [mutateUser]);

  const handleSignOut = async () => {
    try {
      await fetch('/api/auth/signout', {
        method: 'POST',
        credentials: 'include',
      });
      await mutate(() => true, undefined, { revalidate: false });
      navigate('/sign-in');
    } catch (err) {
      console.error('Sign out failed:', err);
    }
  };

  if (!user) {
    return <div className="h-9" />;
  }

  return (
    <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <DropdownMenuTrigger asChild>
        <div className={`flex items-center gap-2 cursor-pointer w-fit rounded-full py-1.5 -mb-1.5 transition-colors ${isSidebarOpen ? 'hover:bg-accent pl-2 pr-6 -mx-2' : 'p-0'}`}>
          <Tooltip open={!isSidebarOpen ? undefined : false}>
            <TooltipTrigger asChild>
              <div className="avatar-lapis size-12 rounded-full flex items-center justify-center text-base font-medium cursor-pointer">
                {user.name
                  ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase()
                  : user.email
                    ? user.email.substring(0, 2).toUpperCase()
                    : 'U'
                }
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={10}>
              {user.name || user.email || 'User'}
            </TooltipContent>
          </Tooltip>
          {isSidebarOpen && (
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-normal truncate max-w-32">
                {user.name || user.email}
              </span>
              <span className="text-xs text-muted-foreground truncate max-w-32 ml-[0.1rem]">
                {user.email}
              </span>
            </div>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="right" className="flex flex-col gap-1 w-64 mb-5 p-2">
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => {
            setIsMenuOpen(false);
            onOpenAccountSettings();
          }}
        >
          <Cog6ToothIcon className="h-4 w-4" />
          <span>Account Settings</span>
        </DropdownMenuItem>
        <Link to="/dashboard/subscription" className="w-full flex items-center">
          <DropdownMenuItem className="w-full flex-1 cursor-pointer">
            <CreditCardIcon className="h-4 w-4" />
            Manage Subscription
          </DropdownMenuItem>
        </Link>
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={handleSignOut}
        >
          <ArrowRightOnRectangleIcon className="h-4 w-4" />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SidebarToggle({ isSidebarOpen, toggleSidebar }: { isSidebarOpen: boolean; toggleSidebar: () => void }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="secondary"
          size="icon"
          onClick={toggleSidebar}
          className="!size-10 !p-0"
        >
          {isSidebarOpen ? <PanelLeft className="h-4 w-4" /> : <PanelRight className="h-4 w-4" />}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={10}>
        {isSidebarOpen ? 'Close menu' : 'Open menu'}
      </TooltipContent>
    </Tooltip>
  );
}

export default function DashboardLayout() {
  const location = useLocation();
  const pathname = location.pathname;
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isAgentPanelOpen, setIsAgentPanelOpen] = useState(() => {
    return localStorage.getItem('agent-panel-open') === 'true';
  });
  const [showAgentGlow, setShowAgentGlow] = useState(() => {
    return !localStorage.getItem('agent-panel-opened');
  });
  const [panelGradientAngle, setPanelGradientAngle] = useState(() => Math.floor(Math.random() * 360));
  const [panelGlowEligible] = useState(() => !localStorage.getItem('agent-panel-glow-seen'));
  const [panelGlowState, setPanelGlowState] = useState<'on' | 'dismissing' | 'pulse' | 'off'>('off');
  const [mounted, setMounted] = useState(false);
  const [isAccountSettingsOpen, setIsAccountSettingsOpen] = useState(false);
  const [accountSettingsSection, setAccountSettingsSection] = useState<string | undefined>();
  const MIN_CONTENT_WIDTH = 600;
  const sidebarWidth = isSidebarOpen ? 268 : 76;

  const getMaxPanelWidth = useCallback(() => {
    return Math.max(320, window.innerWidth - sidebarWidth - MIN_CONTENT_WIDTH - 20);
  }, [sidebarWidth]);


  const [agentPanelWidth, setAgentPanelWidth] = useState(() => Math.min(560, window.innerWidth - 268 - MIN_CONTENT_WIDTH - 20));
  const [isResizing, setIsResizing] = useState(false);

  // Clamp panel width when window resizes or sidebar toggles
  useEffect(() => {
    const maxWidth = getMaxPanelWidth();
    if (agentPanelWidth > maxWidth) {
      setAgentPanelWidth(maxWidth);
    }
  }, [isSidebarOpen, getMaxPanelWidth, agentPanelWidth]);

  useEffect(() => {
    const handleWindowResize = () => {
      const maxWidth = getMaxPanelWidth();
      setAgentPanelWidth(prev => Math.min(prev, maxWidth));
    };
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [getMaxPanelWidth]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = agentPanelWidth;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = startX - e.clientX;
      const maxWidth = Math.max(320, window.innerWidth - sidebarWidth - MIN_CONTENT_WIDTH - 20);
      const newWidth = Math.min(maxWidth, Math.max(320, startWidth + delta));
      setAgentPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [agentPanelWidth, sidebarWidth]);

  const handleAgentPanelToggle = () => {
    if (!isAgentPanelOpen) {
      localStorage.setItem('agent-panel-opened', 'true');
      setShowAgentGlow(false);
      setPanelGradientAngle(Math.floor(Math.random() * 360));
      if (panelGlowEligible) {
        setPanelGlowState('on');
        localStorage.setItem('agent-panel-glow-seen', 'true');
      } else {
        setPanelGlowState('pulse');
      }
    }
    setIsAgentPanelOpen(prev => {
      const next = !prev;
      localStorage.setItem('agent-panel-open', String(next));
      return next;
    });
  };

  // Get user data for unread count
  const { data: user } = useSWR<User>('/api/user', fetcher);
  
  // Get unread conversations count for Primary tab only (is_campaign = true).
  // Polled every 15s so a reply shows on the sidebar badge without a reload.
  const { conversations } = useConversations(user?.id?.toString() || '', '', true, 15000);
  const unreadCount = conversations?.filter(conv => !conv.is_read).length || 0;

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  useEffect(() => {
    setMounted(true);
  }, []);

  const productName = getProductName();

  return (
    <SidebarContext.Provider value={{ isSidebarOpen, toggleSidebar, setSidebarOpen: setIsSidebarOpen, isAgentPanelOpen, mounted }}>
      <div className="flex flex-row min-h-full mx-auto w-full bg-background">
        {/* AI Agent Panel */}
        <div
          className={`fixed top-0 right-0 z-40 h-full p-gutter ${
            isAgentPanelOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <div className="h-full relative" style={{ width: `${agentPanelWidth}px` }}>
            {/* Resize handle - hidden on small screens */}
            {getMaxPanelWidth() > 360 && (
              <div
                className="absolute left-0 top-0 bottom-0 w-2 cursor-col-resize z-50 group"
                onMouseDown={handleResizeStart}
              >
                {!isResizing && <div className="absolute -left-1 top-8 bottom-8 w-1 rounded-full bg-focus-ring opacity-0 group-hover:opacity-100 transition-opacity" />}
              </div>
            )}
            {isAgentPanelOpen && panelGlowState !== 'off' && (
              <div className="absolute inset-[-3px] rounded-card overflow-hidden blur-md pointer-events-none">
                <div
                  className={`absolute inset-[-200%] ${
                    panelGlowState === 'on' ? 'animate-glow-steady' :
                    panelGlowState === 'pulse' ? 'animate-pulse-once' :
                    'animate-glow-dismiss'
                  }`}
                  onAnimationEnd={() => (panelGlowState === 'dismissing' || panelGlowState === 'pulse') && setPanelGlowState('off')}
                  style={{
                    background: `conic-gradient(from ${panelGradientAngle}deg, var(--gradient-agent-glow), #fda4af)`,
                  }}
                />
              </div>
            )}
          <div className="w-full h-full bg-card border border-border shadow-sm rounded-card relative overflow-hidden" onMouseEnter={() => panelGlowState === 'on' && setPanelGlowState('dismissing')}>
            <AIPanel
              isOpen={isAgentPanelOpen}
              onClose={() => {
                setIsAgentPanelOpen(false);
                localStorage.setItem('agent-panel-open', 'false');
              }}
            />
          </div>
          </div>
        </div>

        {/* Sidebar: a flush rectangle, edge to edge on the left, top and bottom,
            with the same inset on both sides of its content (a transparent 1px
            border plus sidebar-inset). The gutter top and bottom keeps the logo
            row centred on each page's first row. Only the right edge is drawn. */}
        <div className="fixed top-0 left-0 h-full z-30">
          <div
            className={`sidebar-no-transition ${
              isSidebarOpen ? 'w-64' : 'w-[72px]'
            } bg-sidebar border border-transparent border-r-divider transition-[width,background-color,border-color] duration-300 ease-in-out relative h-full py-gutter will-change-[width]`}
          >
            <div className="flex flex-col justify-between h-full pb-sidebar-inset pt-sidebar-top">
              {/* Header */}
              <header className="m-0 p-0">
                <div className="mx-auto px-sidebar-inset mb-header-gap h-12 flex justify-between items-center">
                  {isSidebarOpen ? (
                    <>
                      {/* The icon box has ~7px of internal whitespace; the glyph lands a little inside the sidebar inset */}
                      <Link to="/dashboard/campaigns" className="flex items-center">
                        <LogoIcon size={26} />
                        <span className="-ml-1 text-2xl font-medium tracking-tighter text-foreground">{productName}</span>
                      </Link>
                      <SidebarToggle isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
                    </>
                  ) : (
                    <div className="flex justify-center w-full">
                      <SidebarToggle isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
                    </div>
                  )}
                </div>
              </header>

              {/* Workspace. The toggle is 2.5rem tall in a 3rem row, so it sits
                  header-gap plus 0.25rem above the line the page header rules
                  run along; the pill takes the same distance below it. */}
              <div className={`w-full mt-[calc(var(--spacing-header-gap)_+_0.25rem)] transition-[padding] duration-300 ease-in-out ${isSidebarOpen ? 'px-sidebar-inset' : 'flex justify-center'}`}>
                <WorkspaceMenu
                  isSidebarOpen={isSidebarOpen}
                  onOpenAccountSettings={(section) => { if (section) setAccountSettingsSection(section); setIsAccountSettingsOpen(true); }}
                />
              </div>

              {/* Navigation */}
              <nav className={`h-full overflow-y-auto pt-10 w-full overflow-x-hidden transition-[padding] duration-300 ease-in-out ${isSidebarOpen ? 'px-sidebar-inset' : 'flex flex-col items-center'}`}>
                {navItems.map((item, index) => {
                  if ('subheading' in item) {
                    return isSidebarOpen ? (
                      <div key={`subheading-${index}`} className="px-3 py-2 mt-3 mb-0 first:mt-0">
                        <span className="text-[11px] font-semibold text-content-subtle tracking-wide">
                          {item.subheading}
                        </span>
                      </div>
                    ) : (
                      <div key={`subheading-${index}`} className="mt-3 first:mt-0" />
                    );
                  }

                  const isActive = pathname === item.href || pathname.startsWith(item.href + '/');

                  const buttonContent = (
                    <Button
                      variant={isActive ? 'secondary' : 'ghost'}
                      className={`shadow-none my-0.5 h-11 ${
                        isSidebarOpen ? 'w-full justify-start' : 'w-11 justify-center'
                      } rounded-sm px-4 transition-[width,height,padding] !border-none duration-300 ease-in-out ${
                        item.disabled
                          ? 'opacity-40 !cursor-default'
                          : isActive
                            ? '!text-info-solid !bg-sidebar-active !bg-none hover:!bg-sidebar-active cursor-pointer'
                            : 'hover:!bg-transparent cursor-pointer'
                      } ${!isSidebarOpen ? 'px-0' : ''}`}
                    >
                      <div className="relative">
                        <item.icon className={`h-4.5 w-4.5 font-size-4 ${isActive && !item.disabled ? 'text-info-solid' : ''}`} />
                        {item.href === '/dashboard/mailbox' && unreadCount > 0 && !isSidebarOpen && (
                          <span className="absolute -top-2 -right-0.5 px-1 min-w-4 h-4 bg-danger-solid text-on-accent text-[10px] rounded-full flex items-center justify-center font-medium transform translate-x-1/2">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </div>
                      {isSidebarOpen && (
                        <div className="flex items-center">
                          <span className="ml-0.5 font-normal">{item.label}</span>
                          {item.href === '/dashboard/mailbox' && unreadCount > 0 && (
                            <span className="ml-2 h-5 min-w-5 px-1 bg-danger-solid text-on-accent text-xs rounded-full flex items-center justify-center font-medium">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          )}
                        </div>
                      )}
                    </Button>
                  );

                  return (
                    <Tooltip key={item.href + item.label}>
                      <TooltipTrigger asChild>
                        {item.disabled ? (
                          <div>{buttonContent}</div>
                        ) : (
                          <Link to={item.href}>{buttonContent}</Link>
                        )}
                      </TooltipTrigger>
                      <TooltipContent side="right" sideOffset={10}>
                        {item.tooltip || item.label}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}

              </nav>

              {/* Footer */}
              <div className={`flex flex-col gap-3 ${isSidebarOpen ? 'items-start px-sidebar-inset' : 'items-center px-sidebar-inset'}`}>

                <CreditDisplay isSidebarOpen={isSidebarOpen} onViewHistory={() => {
                  setAccountSettingsSection('usage');
                  setIsAccountSettingsOpen(true);
                }} />

                <UserMenu
                  isSidebarOpen={isSidebarOpen}
                  onOpenAccountSettings={(section) => { if (section) setAccountSettingsSection(section); setIsAccountSettingsOpen(true); }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main content. Offset by exactly the sidebar's width, so the page
            and flush tables meet the sidebar's edge with no strip between. */}
        <main
          className={`main-no-transition flex-1 overflow-y-auto h-screen bg-background ${
            isResizing ? '' : 'transition-[margin,padding] duration-300 ease-in-out'
          } ${
            isSidebarOpen ? 'ml-64' : 'ml-[72px]'
          }`}
          style={{
            marginRight: isAgentPanelOpen ? `${agentPanelWidth + 4}px` : undefined,
            minWidth: `${MIN_CONTENT_WIDTH}px`,
          }}
        >
          <Outlet />
        </main>
      </div>
      
      {/* Floating AI Chat Button — hidden for now while the agent page is the
          primary chat surface. Re-enable by removing the `false &&` below. */}
      {false && !isAgentPanelOpen && (
        <div className="fixed bottom-6 right-6 z-40">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="relative">
                {showAgentGlow && (
                  <>
                    <div className="absolute inset-0 rounded-full overflow-hidden">
                      <div className="absolute inset-[-200%] animate-glow-intro" style={{
                        background: 'conic-gradient(from 0deg, var(--gradient-agent-glow), #fda4af)',
                      }} />
                    </div>
                    <div className="absolute inset-[-2px] rounded-full overflow-hidden blur-sm opacity-60">
                      <div className="absolute inset-[-200%] animate-glow-intro" style={{
                        background: 'conic-gradient(from 0deg, var(--gradient-agent-glow), #fda4af)',
                      }} />
                    </div>
                  </>
                )}
                <button
                  onClick={handleAgentPanelToggle}
                  className="relative z-10 h-12 px-5 rounded-full bg-surface-raised shadow-lg hover:shadow-xl flex items-center gap-2 transition-shadow cursor-pointer"
                >
                  <Sparkles className="h-4 w-4 text-content-body" />
                  <span className="text-sm font-medium text-content-body">AI Chat</span>
                </button>
              </div>
            </TooltipTrigger>
            <TooltipContent side="left" sideOffset={10}>
              AI Chat
            </TooltipContent>
          </Tooltip>
        </div>
      )}

      {/* Account Settings Dialog */}
      <AccountSettingsDialog
        isOpen={isAccountSettingsOpen}
        setIsOpen={(open) => {
          setIsAccountSettingsOpen(open);
          if (!open) setAccountSettingsSection(undefined);
        }}
        initialSection={accountSettingsSection}
      />
    </SidebarContext.Provider>
  );
}
